/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useMemo } from "react";

// Types
import { PlanPurchaseConfirmationProps } from "@swiggy-private/dls-web-recipe-common-web/dist/components/SwiggyOne/PlanRemoveConfirmationBottomsheet";
import { IRemovePlanMessage, PlanDetails } from "@swiggy-private/cart-types";
import { OneMembershipHandlers } from "../types";

// Constants
import {
    BOTTOMSHEET_BLCK_CTAS_TEXTS,
    BOTTOMSHEET_CTAS_TEXTS,
} from "@super-ui/components/swiggy-one-widget-dls-wrapper/constants";

// Helpers
import { headerBuilder } from "@super-ui/components/swiggy-one-widget-dls-wrapper/helpers/bottomsheetNodeBuilder";
import { isEmpty } from "@swiggy-private/js-utils";
import { isSwiggyBlack } from "@super-ui/components/swiggy-one-widget-dls-wrapper";
import { getCTA } from "@super-ui/helpers/general";

// Components
import { RemoveHalfcardTitle } from "../Components/RemoveHalfcardTitle";

interface PlanRemovalAnalytics {
    onImpressionPlanRemovalHalfcard: (context: string) => void;
    onClickRemovePlanRemovalHalfcard: (context: string) => void;
}

interface Handlers extends OneMembershipHandlers {
    setShowRemovePlanConfirmation: (value: boolean) => void;
    nudgeAnalytics: PlanRemovalAnalytics;
}

interface GetRemoveBSProps {
    removePlanMessage: IRemovePlanMessage;
    handlers: Handlers;
    planDetails: PlanDetails;
    showRemovePlanConfirmation: boolean;
}

export const useGetRemoveBSProps = ({
    removePlanMessage,
    handlers,
    showRemovePlanConfirmation,
    planDetails,
}: GetRemoveBSProps) => {
    const {
        removeSuperFromCart,
        setShowRemovePlanConfirmation,
        nudgeAnalytics: { onImpressionPlanRemovalHalfcard, onClickRemovePlanRemovalHalfcard },
    } = handlers;

    // icon
    const icon = useMemo(() => removePlanMessage?.logo_ctx?.logo || "", [removePlanMessage?.logo_ctx?.logo]);

    // Building the title and header information
    const headerTitle = useMemo(() => {
        const titleText = removePlanMessage?.title?.text || "";
        const titleUnicode = removePlanMessage?.title?.attribute_values?.unicode || "";
        return headerBuilder(titleText, titleUnicode);
    }, [removePlanMessage]);

    const isSwiggyBlckPlan = isSwiggyBlack(planDetails);

    // Extracting and preparing content-related nodes
    const contentDetails = useMemo(() => {
        const content = removePlanMessage?.content?.[0] || {};
        const subTitle = content.sub_title || "";

        return {
            contentTitle: content?.title,
            subTitle,
            contentTitleNode: <RemoveHalfcardTitle content={content} isSwiggyBlackType={isSwiggyBlckPlan} />,
        };
    }, [isSwiggyBlckPlan, removePlanMessage?.content]);

    // Common context used in analytics events
    const commonContext = useMemo(
        () => ({
            copy1: headerTitle,
            main_copy: contentDetails.contentTitle,
            copy2: contentDetails.subTitle,
            variant: removePlanMessage?.variant || "-",
            initialBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_not_added || "-",
            finalBillAmount: removePlanMessage?.attribute_values?.cart_total_plan_added || "-",
        }),
        [headerTitle, contentDetails, removePlanMessage],
    );

    // Creating the context for analytics events
    const createContext = useCallback(
        (payload: Record<string, string>) => JSON.stringify({ ...commonContext, ...payload }),
        [commonContext],
    );

    const BottomsheetCtaTexts = useMemo(
        () => (isSwiggyBlckPlan ? BOTTOMSHEET_BLCK_CTAS_TEXTS : BOTTOMSHEET_CTAS_TEXTS),
        [isSwiggyBlckPlan],
    );

    const hasSignificantCartDifference =
        !isEmpty(removePlanMessage) &&
        !isEmpty(removePlanMessage.attribute_values) &&
        Number(removePlanMessage.attribute_values.plan_in_cart_savings) >
            Number(removePlanMessage.attribute_values.cart_total_diff_config);

    const { primaryCTA, secondaryCTA } = getCTA({
        removePlanMessage,
        hasSignificantCartDifference,
        planType: planDetails?.type,
        bottomsheetCtaTexts: BottomsheetCtaTexts,
    });

    // Primary CTA handler
    const planRemovalConfirmationPrimaryCTA = useCallback(() => {
        setShowRemovePlanConfirmation(false);
        onClickRemovePlanRemovalHalfcard(createContext({ cta_clicked: primaryCTA }));
    }, [createContext, onClickRemovePlanRemovalHalfcard, setShowRemovePlanConfirmation, primaryCTA]);

    // Secondary CTA handler
    const planRemovalConfirmationSecondaryCTA = useCallback(() => {
        setShowRemovePlanConfirmation(false);
        removeSuperFromCart(secondaryCTA);
        onClickRemovePlanRemovalHalfcard(createContext({ cta_clicked: secondaryCTA }));
    }, [
        secondaryCTA,
        createContext,
        onClickRemovePlanRemovalHalfcard,
        removeSuperFromCart,
        setShowRemovePlanConfirmation,
    ]);

    // Preparing bottom sheet props to return
    const bottomSheetProps = useMemo(
        () => ({
            icon: icon,
            title: {
                text: headerTitle,
            },
            content: [
                {
                    title: contentDetails.contentTitleNode,
                    subTitle: contentDetails.subTitle,
                },
            ],
            ctas: {
                primaryCTA: {
                    text: BottomsheetCtaTexts.PRIMARY,
                    handler: planRemovalConfirmationPrimaryCTA,
                },
                secondaryCTA: {
                    text: BottomsheetCtaTexts.SECONDARY,
                    handler: planRemovalConfirmationSecondaryCTA,
                },
            },
            closeOnOverlayClick: false,
        }),
        [
            icon,
            headerTitle,
            contentDetails.contentTitleNode,
            contentDetails.subTitle,
            BottomsheetCtaTexts,
            planRemovalConfirmationPrimaryCTA,
            planRemovalConfirmationSecondaryCTA,
        ],
    );

    // Determine if the plan removal bottom sheet should be shown
    const allowPlanRemovalBottomsheet = useMemo(
        () =>
            showRemovePlanConfirmation &&
            !isEmpty(bottomSheetProps?.title) &&
            !isEmpty(bottomSheetProps?.content[0]?.title),
        [bottomSheetProps, showRemovePlanConfirmation],
    );

    // Impression event for plan removal half card
    useEffect(() => {
        if (allowPlanRemovalBottomsheet) {
            onImpressionPlanRemovalHalfcard(createContext({ cta1: primaryCTA, cta2: secondaryCTA }));
        }
    }, [
        primaryCTA,
        secondaryCTA,
        allowPlanRemovalBottomsheet,
        createContext,
        onImpressionPlanRemovalHalfcard,
        showRemovePlanConfirmation,
    ]);

    return { bottomSheetProps: bottomSheetProps as PlanPurchaseConfirmationProps, allowPlanRemovalBottomsheet };
};
